import React, { useState } from 'react';
import {
  Dialog,
  Box,
  TextField,
  Button,
  MenuItem,
  Typography
} from '@mui/material';
import { styled } from '@mui/system';
import { useApiManager } from 'lib/ApiContext';

// v2: el verde Matrix se retira — paleta de marca (docs/IDENTIDAD_VISUAL.md §9)
const StyledBox = styled(Box)({
  backgroundColor: '#14141F',
  padding: '2rem',
  width: 'min(600px, 92vw)',
  color: '#EDE9F7',
  fontFamily: 'monospace',
  border: '1px solid rgba(237,233,247,0.08)',
  borderRadius: '10px',
  '@media (max-width: 480px)': {
    width: '100%',
    padding: '1rem',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxSizing: 'border-box',
  },
});

const StyledTextField = styled(TextField)({
  '& .MuiInputLabel-root': {
    color: '#A8A3B8',
    transform: ' scale(0.75)',
    transformOrigin: 'top left',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#C77DFF',
  },
  '& .MuiInputBase-input, & textarea': {
    color: '#EDE9F7',
    fontFamily: 'monospace',
    backgroundColor: 'transparent',
    padding: '6px',
  },
  '& input::placeholder, & textarea::placeholder': {
    color: '#6E6880',
    opacity: 1,
  },
  '& .MuiInput-underline:before, & .MuiInput-underline:after': {
    borderBottom: '1px solid rgba(199,125,255,.4)',
  },
  '& .MuiInputBase-root.Mui-focused:after': {
    borderBottom: '1px solid rgba(199,125,255,.4)',
  },
  '& .MuiSvgIcon-root': {
    color: '#A8A3B8',
  },
  '& .MuiFormHelperText-root': {
    color: '#C77DFF',
    fontFamily: 'monospace',
  },
  '@media (max-width: 480px)': {
    '& .MuiInputLabel-root': {
      fontSize: '0.85rem',
    },
    '& .MuiInputBase-input, & textarea': {
      fontSize: '0.9rem',
      padding: '8px 6px',
    },
  },
});

const StyledButton = styled(Button)({
  marginTop: '1rem',
  color: '#C77DFF',
  borderColor: 'rgba(199,125,255,.4)',
  fontFamily: 'monospace',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'rgba(157,0,255,.08)',
  },
  '@media (max-width: 480px)': {
    fontSize: '0.9rem',
    marginTop: '0.75rem',
    padding: '0.6rem',
    minHeight: '44px',
  },
});

const publishTypes = [
  { value: 0, label: 'Solo texto' },
  { value: 1, label: 'Imágenes (no implementado)' },
  { value: 2, label: 'Video' },
];

const NewPublicationDialog = ({ open, onClose }) => {
  const api = useApiManager();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [publishType, setPublishType] = useState(0);
  const [videoUrl, setVideoUrl] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const getTodayDate = () => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const handleSubmit = async () => {
    setErrorMsg('');
    if (!title || !description) {
      setErrorMsg('Título y descripción son obligatorios.');
      return;
    }
    if (publishType === 2 && !videoUrl) {
      setErrorMsg('Debe ingresar una URL de video.');
      return;
    }

    const payload = {
      PublicationID: Date.now().toString(),
      DateAdded: getTodayDate(),
      Title: title,
      Description: description,
      EventID: '',
      Published: 1,
      PublishType: publishType,
      VideoURL: publishType === 2 ? videoUrl : '',
    };

    try {
      await api.postPublish(payload);
      onClose();
    } catch (error) {
      console.error(error);
      setErrorMsg('Error al publicar. Intente de nuevo.');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      sx={{
        backgroundColor: 'transparent',
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
        },
        '& .MuiDialog-container': {
          alignItems: 'center',
          justifyContent: 'center',
        },
        '& .MuiDialog-paper': {
          margin: 0,
          backgroundColor: 'transparent',
          boxShadow: 'none',
          '@media (max-width: 480px)': {
            margin: '1rem',
            maxWidth: 'calc(100vw - 2rem)',
          },
        },
      }}
    >
      <StyledBox>
        <Typography 
          variant="h6" 
          sx={{
            color: '#C77DFF',
            fontFamily: 'monospace',
            mb: 2,
            '@media (max-width: 480px)': {
              fontSize: '1.1rem',
              mb: 1.5,
            },
          }}
        >
          *** Nueva Publicación ***
        </Typography>

        <StyledTextField
            variant="standard"
            label="Título"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            slotProps={{
                inputLabel: {
                shrink: true,
                },
            }}
        />

        <StyledTextField
          fullWidth
          variant="standard"
          label="Descripción"
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mt: 2 }}
        />

        <StyledTextField
          select
          fullWidth
          variant="standard"
          label="Tipo de publicación"
          value={publishType}
          onChange={(e) => setPublishType(Number(e.target.value))}
          sx={{ mt: 2 }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  backgroundColor: '#1E1B2E',
                  border: '1px solid rgba(237,233,247,0.08)',
                  '& .MuiMenuItem-root': {
                    color: '#EDE9F7',
                    fontFamily: 'monospace',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    padding: '12px 16px',
                    '&:hover': {
                      backgroundColor: 'rgba(157,0,255,.08)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(157,0,255,.16)',
                      '&:hover': {
                        backgroundColor: 'rgba(157,0,255,.24)',
                      },
                    },
                  },
                },
              },
            },
          }}
        >
          {publishTypes.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </StyledTextField>

        {publishType === 2 && (
          <StyledTextField
            fullWidth
            variant="standard"
            label="Video URL"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            sx={{ mt: 2 }}
          />
        )}

        {errorMsg && (
          <Typography 
            sx={{ 
              mt: 2, 
              color: 'red', 
              fontFamily: 'monospace',
              '@media (max-width: 480px)': {
                fontSize: '0.85rem',
                mt: 1.5,
              },
            }}
          >
            {errorMsg}
          </Typography>
        )}

        <StyledButton fullWidth variant="outlined" onClick={handleSubmit}>
          Publicar
        </StyledButton>

        <StyledButton fullWidth variant="outlined" onClick={onClose}>
          Cancelar
        </StyledButton>
      </StyledBox>
    </Dialog>
  );
};

export default NewPublicationDialog;